import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { TextField, InputAdornment, CircularProgress, Box, Theme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { debounce } from "lodash";
import { CheckCircleRounded, CancelRounded } from "@mui/icons-material";
import { withCursorPosition } from "../../../../../../../../shell/components/withCursorPosition";
import { FieldShell } from "../../../../components/Editor/Field/FieldShell";
import { searchItems } from "../../../../../../../../shell/store/content";
import { notify } from "../../../../../../../../shell/store/notifications";
import { AppState } from "../../../../../../../../shell/store/types";
import { ContentItemWithDirtyAndPublishing } from "../../../../../../../../shell/services/types";
import { useDomain } from "../../../../../../../../shell/hooks/use-domain";
import { Error } from "../../../../components/Editor/Field/FieldShell";
import { hasErrors } from "./util";
import { useTranslation } from "react-i18next";
const TextFieldWithCursorPosition = withCursorPosition(TextField);
type ItemRouteProps = {
  onChange: (value: string, name: string) => void;
  error: Error;
  onUpdateErrors: (name: string, error: Error) => void;
};
export const ItemRoute = ({
  onChange,
  error,
  onUpdateErrors
}: ItemRouteProps) => {
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const {
    itemZUID,
    modelZUID
  } = useParams<{
    itemZUID: string;
    modelZUID: string;
  }>();
  const domain = useDomain();
  const location = useLocation();
  const isCreateItemPage = location?.pathname?.split("/")?.pop() === "new";
  const items = useSelector((state: AppState) => state.content);
  const item = items[isCreateItemPage ? `new:${modelZUID}` : itemZUID];
  const [pathPart, setPathPart] = useState(item?.web?.pathPart);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnique, setIsUnique] = useState(true);
  const parent = items[item?.web?.parentZUID];
  const validate = useCallback(debounce(path => {
    if (!path) {
      setIsUnique(false);
      return;
    }
    const fullPath = parent ? `${parent.web?.path}${path}/` : `/${path}/`;
    setIsLoading(true);
    return dispatch(searchItems(fullPath))
    // @ts-expect-error untyped
    .then(res => {
      if (res?.data) {
        if (Array.isArray(res.data) && res.data.length) {
          // check list of partial matches for exact path match
          const matches = res.data.filter((_item: ContentItemWithDirtyAndPublishing) => {
            /**
             * Exclude currently viewed item zuid, as it's currently saved path would match.
             * Check if other results have a matching path, if so then it is already taken and
             * can not be used.
             * Check if the matched result is a sibling of the currently viewed item, if so then
             * it is fine to have the same path.
             * Result paths come with leading and trailing slashes
             */
            return _item.meta.ZUID !== item?.meta?.ZUID && !Object.values(_item.siblings || {}).includes(item?.meta?.ZUID) && _item.web.path === fullPath;
          });
          setIsUnique(!matches.length);
          onUpdateErrors("pathPart", {
            CUSTOM_ERROR: !!matches.length ? t("content.itemEditMetaUrlPathTaken") : ""
          });
        } else {
          setIsUnique(true);
          onUpdateErrors("pathPart", {
            CUSTOM_ERROR: ""
          });
        }
      } else {
        dispatch(notify({
          kind: "warn",
          message: t("content.itemEditMetaApiFailedWithStatus", {
            status: res ? res.status : ""
          })
        }));
      }
    }).finally(() => setIsLoading(false));
  }, 1000), [parent, t]);
  const handleInputChange = (evt: ChangeEvent<HTMLInputElement>) => {
    // All URLs are lowercased
    // Replace ampersand characters with 'and'
    // Only allow alphanumeric characters
    const path = evt.target.value.toLowerCase().replace(/\&/g, "and").replace(/[^a-zA-Z0-9]/g, "-");
    validate(path);
    setPathPart(path);
    onChange(path, "pathPart");
  };

  // Revalidate when parent path changes
  useEffect(() => {
    validate(pathPart);
  }, [parent?.web, pathPart]);
  useEffect(() => {
    setPathPart(item?.web?.pathPart || "");
  }, [item?.web]);
  return <Box id="pathPart" data-cy="pathPart" data-sentry-element="Box" data-sentry-component="ItemRoute" data-sentry-source-file="ItemRoute.tsx">
      <FieldShell settings={{
      label: t("content.itemEditMetaUrlPathPart"),
      required: true
    }} customTooltip={t("content.itemEditMetaUrlPathPartTooltip")} withInteractiveTooltip={false} errors={error} data-sentry-element="FieldShell" data-sentry-source-file="ItemRoute.tsx">
        <TextFieldWithCursorPosition type="text" name="pathPart" value={pathPart} onChange={handleInputChange} variant="outlined" color="primary" fullWidth InputProps={{
        startAdornment: <Adornment isLoading={isLoading} isUnique={isUnique} />
      }} slotProps={{
        formHelperText: {
          variant: "body2",
          sx: {
            color: (theme: Theme) => theme.palette.info.dark
          }
        }
      }} helperText={!!pathPart && isUnique && `${domain}${parent ? parent.web?.path + pathPart : `/${pathPart}`}`} error={hasErrors(error)} data-sentry-element="TextFieldWithCursorPosition" data-sentry-source-file="ItemRoute.tsx" />
      </FieldShell>
    </Box>;
};
type AdornmentProps = {
  isLoading: boolean;
  isUnique: boolean;
};
const Adornment = ({
  isLoading,
  isUnique
}: AdornmentProps) => {
  if (isLoading) {
    return <InputAdornment position="start" sx={{
      minWidth: 32,
      justifyContent: "center"
    }}>
        <CircularProgress size={20} />
      </InputAdornment>;
  }
  return <InputAdornment position="start" data-sentry-element="InputAdornment" data-sentry-component="Adornment" data-sentry-source-file="ItemRoute.tsx">
      {isUnique ? <CheckCircleRounded color="success" /> : <CancelRounded color="error" />}
    </InputAdornment>;
};